import { Alert, AlertTitle, AlertDescription } from '@design-system';
import { type AssetTypeId, getCalcFormulaInfo } from '@/features/funding-sources';

function FormulaLine({ label, expression }: { label: string; expression: string }) {
  return (
    <div className="bg-muted flex items-center gap-3 rounded-lg border p-3">
      <span className="text-sm font-semibold italic text-gray-950">{label}</span>
      <span className="text-foreground text-lg">=</span>
      <span className="text-sm text-gray-950">{expression}</span>
    </div>
  );
}

export interface CalcFormulaSheetContentProps {
  assetTypeId: AssetTypeId;
  label: string;
  hasContributions: boolean;
  hasContributionStop: boolean;
  hasWithdrawals: boolean;
  isIncomeStream: boolean;
  /** When false, the income-stream formula is rendered without the growth term. */
  showsIncomeGrowth?: boolean;
}

export function CalcFormulaSheetContent({
  assetTypeId,
  hasContributions,
  hasContributionStop,
  hasWithdrawals,
  isIncomeStream,
  showsIncomeGrowth = true,
}: CalcFormulaSheetContentProps) {
  const info = getCalcFormulaInfo(assetTypeId);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-foreground text-sm leading-relaxed">{info.description}</p>
      </div>

      {isIncomeStream ? (
        <IncomeStreamFormulas showsGrowth={showsIncomeGrowth} />
      ) : (
        <InvestmentFormulas
          hasContributions={hasContributions}
          hasContributionStop={hasContributionStop}
          hasWithdrawals={hasWithdrawals}
        />
      )}

      <Alert variant="info">
        <AlertTitle>Tax treatment</AlertTitle>
        <AlertDescription>{info.taxExplanation}</AlertDescription>
      </Alert>
    </div>
  );
}

function IncomeStreamFormulas({ showsGrowth }: { showsGrowth: boolean }) {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-950">Formulas</h4>

      {showsGrowth ? (
        <>
          <FormulaLine
            label="Yearly Income"
            expression="Annual Income x (1 + Growth Rate) ^ (Years Since Reference Age)"
          />
          <FormulaLine
            label="Gross Total"
            expression="Sum of Yearly Income over the care period"
          />
        </>
      ) : (
        <>
          <FormulaLine
            label="Monthly Income"
            expression="Annual Income / 12"
          />
          <FormulaLine
            label="Gross Total"
            expression="Monthly Income x Care Months"
          />
        </>
      )}

      <FormulaLine
        label="After-Tax Value"
        expression="Gross Total x (1 - Tax Rate)"
      />
    </div>
  );
}

function InvestmentFormulas({
  hasContributions,
  hasContributionStop,
  hasWithdrawals,
}: {
  hasContributions: boolean;
  hasContributionStop: boolean;
  hasWithdrawals: boolean;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 font-semibold text-gray-950">Year-by-year simulation</h4>
        <p className="text-foreground mb-3 text-sm">
          Each year until LTC start, the following is applied:
        </p>
        <ol className="list-decimal space-y-2 pl-5">
          <li className="text-foreground text-sm leading-relaxed">
            Apply growth to the full balance.
          </li>
          {hasContributions && (
            <li className="text-foreground text-sm leading-relaxed">
              Add yearly contributions
              {hasContributionStop ? ' (until the stop year).' : '.'}
            </li>
          )}
          {hasWithdrawals && (
            <li className="text-foreground text-sm leading-relaxed">
              Subtract yearly withdrawals during the withdrawal period.
            </li>
          )}
          <li className="text-foreground text-sm leading-relaxed">
            After the final year, apply the estimated tax rate.
          </li>
        </ol>
      </div>

      <h4 className="font-semibold text-gray-950">Formulas</h4>

      <FormulaLine
        label="Year Growth"
        expression="Balance x (1 + Annual Growth Rate)"
      />

      {hasContributions && (
        <FormulaLine
          label="After Contributions"
          expression="Balance + Yearly Contribution"
        />
      )}

      {hasWithdrawals && (
        <FormulaLine
          label="After Withdrawals"
          expression="Balance - Yearly Withdrawal"
        />
      )}

      <FormulaLine
        label="After-Tax Value"
        expression="Balance - (Taxable Amount x Tax Rate)"
      />
    </div>
  );
}
